'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search as SearchIcon } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';

import type { SearchOfferItem } from '@nutralab/api';
import { Flex, Icon, Input, Popover, Text } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import { SearchCartControl } from './_components/SearchCartControl';
import { SearchEmptyState } from './_components/SearchEmptyState';
import { SearchResultPrice } from './_components/SearchResultPrice';
import styles from './CatalogSearch.module.scss';

import { searchCatalog } from '@/shared/api/actions/search';
import { AppLayer } from '@/shared/constants';
import { useDebouncedValue } from '@/shared/hooks';
import { getCatalogItemPath, getImageUrl } from '@/shared/utils';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 2;

export function CatalogSearch(): React.ReactElement {
    const { t } = useTranslation();

    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState<SearchOfferItem[]>([]);

    const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
    const requestSeqRef = useRef(0);

    useEffect(() => {
        const trimmed = debouncedQuery.trim();

        if (trimmed.length < MIN_QUERY_LENGTH) {
            requestSeqRef.current += 1;
            setIsOpen(false);
            setResults([]);
            return;
        }

        const seq = ++requestSeqRef.current;
        setIsLoading(true);
        setIsOpen(true);

        searchCatalog(trimmed)
            .then((res) => {
                if (seq !== requestSeqRef.current) {
                    return;
                }
                setResults(res.items);
            })
            .catch(() => {
                if (seq !== requestSeqRef.current) {
                    return;
                }
                toast.error(t('error.general'));
            })
            .finally(() => {
                if (seq !== requestSeqRef.current) {
                    return;
                }
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery]);

    const closeSearch = (): void => {
        setIsOpen(false);
        setQuery('');
    };

    return (
        <Popover
            zIndex={AppLayer.POPOVER}
            open={isOpen}
            onOpenChange={(open) => {
                if (open) {
                    setIsOpen(true);
                } else {
                    closeSearch();
                }
            }}
            asAnchor
            align="start"
            className={styles['popover-content']}
            trigger={
                <div className={styles['search-anchor']}>
                    <Input
                        left={
                            isLoading ? (
                                <Icon
                                    icon={Loader2}
                                    color="muted"
                                    className={classNames(styles['search-icon'], styles.spin, 'ml-3')}
                                />
                            ) : (
                                <Icon
                                    icon={SearchIcon}
                                    color="muted"
                                    className={classNames(styles['search-icon'], 'ml-3')}
                                />
                            )
                        }
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('landing.search.placeholder')}
                        aria-label={t('landing.search.placeholder')}
                    />
                </div>
            }
        >
            {isLoading && results.length === 0 ? (
                <Flex justify="center" className={styles.loading}>
                    <Icon icon={Loader2} size="xl" color="muted" className={styles.spin} />
                </Flex>
            ) : results.length > 0 ? (
                <div className={styles.results}>
                    {results.map((item) => {
                        const href = getCatalogItemPath(item);
                        const summary = (
                            <>
                                <img src={getImageUrl(item.main_image)} alt={item.name} className={styles.thumbnail} />

                                <div className={styles['result-info']}>
                                    <Text className={styles.title} truncate>
                                        {item.name}
                                    </Text>
                                    <SearchResultPrice
                                        className={styles.price}
                                        price={item.price}
                                        oldPrice={item.old_price}
                                        expiresAt={item.price_expires_at}
                                    />
                                </div>
                            </>
                        );

                        return (
                            <div key={`${item.type}:${item.id}`} className={styles['result-item']}>
                                {href ? (
                                    <Link href={href} className={styles['result-link']} onClick={closeSearch}>
                                        {summary}
                                    </Link>
                                ) : (
                                    <div className={styles['result-link']}>{summary}</div>
                                )}

                                <div className={styles['cart-slot']}>
                                    <SearchCartControl id={item.id} type={item.type} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <SearchEmptyState />
            )}
        </Popover>
    );
}
